import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  ViewChild,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { isPlatformServer, Location } from '@angular/common';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { ModalButtons } from '@fcom/ui-components/components/modal/interfaces';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import type { AppState } from '@fcom/core/interfaces/app-state.interface';
import type { Profile } from '@fcom/core-api/login/interfaces/login.interface';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors/login.selector';
import { equals } from '@fcom/core/utils/equals';

import { ElementActions, ElementTypes, GaContext } from '../../interfaces/gtm.interface';
import { GtmService } from '../../gtm/services/gtm.service';
import { HotjarService } from '../../services/hotjar/hotjar.service';
import { MediaQueryService } from '../../services/media-query/media-query.service';
import {
  getQualtricsUrl,
  messageAllowedForSurvey,
  QUALTRICS_DOMAIN,
  QualtricsMessageTypes,
  QualtricsSurvey,
} from '../qualtrics.utils';
import { MultivariateTestService } from '../../multivariate-test/services/multivariate-test.service';
import { Segment } from '../../interfaces/multivariate-test.interface';
import { QualtricsPostMessage } from './qualtrics.interface';

@Component({
  selector: 'fin-qualtrics',
  templateUrl: './qualtrics.component.html',
  styleUrls: ['./qualtrics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QualtricsComponent implements OnInit, OnDestroy, OnChanges {
  private languageService = inject(LanguageService);
  private mediaQueryService = inject(MediaQueryService);
  private gtmService = inject(GtmService);
  private renderer = inject(Renderer2);
  private platform = inject(PLATFORM_ID);
  private location = inject(Location);
  private hotjarService = inject(HotjarService);
  private multivariateTestService = inject(MultivariateTestService);
  private store$ = inject<Store<AppState>>(Store);

  private csatScore = '';

  @Input()
  survey: QualtricsSurvey = { name: '', url: '' };

  @Input()
  set open(open: boolean) {
    if (open) {
      this.openModal();
    }
  }

  @Output()
  openChange: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('surveyContainer') iframe;

  readonly ModalButtons = ModalButtons;
  readonly ElementTypes = ElementTypes;
  readonly ElementActions = ElementActions;

  qualtricsUrl$: Observable<string>;
  removeGlobalListener?: () => void;
  previousEvent: unknown = {};
  modalOpen = false;
  private survey$: BehaviorSubject<QualtricsSurvey>;

  constructor() {
    effect(() => {
      this.mediaQueryService.viewportSize();
      untracked(() => {
        this.adjustIframeHeight();
      });
    });
  }

  ngOnInit(): void {
    this.survey$ = new BehaviorSubject<QualtricsSurvey>(this.survey);

    const profile$ = this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), startWith(undefined as Profile | undefined));
    this.qualtricsUrl$ = combineLatest([
      this.survey$.pipe(
        filter(Boolean),
        distinctUntilChanged((a, b): boolean => a.name === b.name && a.url === b.url)
      ),
      profile$,
    ]).pipe(
      switchMap(([survey, profile]) =>
        this.languageService.translate(survey.url).pipe(
          filter(Boolean),
          switchMap((url) =>
            this.multivariateTestService.segmentsFeed$.pipe(
              take(1),
              map(
                (segment) =>
                  getQualtricsUrl(
                    this.languageService.langKeyValue,
                    this.languageService.langValue,
                    String(url),
                    segment.segment as unknown as Segment,
                    this.hotjarService.getUserId(),
                    profile
                  ) + this.embeddedData()
              )
            )
          )
        )
      )
    );

    this.watchPostMessages();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['survey'] && !changes['survey'].firstChange) {
      this.survey$.next(changes['survey'].currentValue);
    }
  }

  ngOnDestroy(): void {
    if (this.removeGlobalListener) {
      this.removeGlobalListener();
    }
  }

  embeddedData(): string {
    const path: string = this.location.path();
    const stripParams: string = path.split('?')[0];
    const splitPath: string[] = stripParams.split('/');
    const pagePath: string = path.includes('/int/gb/') || splitPath.length <= 2 ? path : splitPath.slice(2).join('/');

    /**
     * Send additional data to Qualtrics
     * Can be used from survey if instructed to extract the embedded data
     * current_page_url = full path with params stripped of page ex: /fi-en/url/path
     * pagePath = path with locale-lang removed ex: url/path
     * locale = countrySite code ex: FI
     */
    return `&current_page_url=${stripParams}&pagePath=${pagePath}&locale=${this.languageService.ddsLocaleValue.countrySite}`;
  }

  openModal(): void {
    this.modalOpen = true;

    this.gtmService.trackElement(
      this.survey.name,
      GaContext.QUALTRICS,
      ElementTypes.SURVEY,
      undefined,
      ElementActions.OPEN
    );
  }

  onClose(): void {
    this.modalOpen = false;

    this.openChange.emit(false);

    this.gtmService.trackElement(
      this.survey.name,
      GaContext.QUALTRICS,
      ElementTypes.SURVEY,
      undefined,
      ElementActions.CLOSE
    );
  }

  surveyLoaded(): void {
    this.adjustIframeHeight();
  }

  private adjustIframeHeight(): void {
    const parentHeight: number = this.iframe?.nativeElement.parentElement?.offsetHeight;
    if (!parentHeight) {
      return;
    }
    const margin = 8;
    this.iframe.nativeElement.style.minHeight = 'auto';
    this.iframe.nativeElement.style.height = `${parentHeight - margin}px`;
  }

  private watchPostMessages() {
    if (isPlatformServer(this.platform)) {
      return;
    }

    this.removeGlobalListener = this.renderer.listen('window', 'message', (event) => {
      this.processMessage(event);
    });
  }

  private processMessage(event: QualtricsPostMessage) {
    if (event.origin !== QUALTRICS_DOMAIN) {
      return;
    }

    const key = event.message ? 'message' : 'data';
    const data = event[key];

    if (!data) {
      return;
    }
    const currentMessage = Object.keys(data)[0];
    const eventData = event.data;

    if (
      this.messageHasRealValue(currentMessage) &&
      !equals(this.previousEvent, event.data) &&
      messageAllowedForSurvey(this.survey.name, currentMessage)
    ) {
      this.previousEvent = event.data; // Sometimes qualtrics sends duplicate messages, so we filter those out by saving the previous one

      switch (currentMessage) {
        case QualtricsMessageTypes.OPTIN_RESPONSE:
          this.gtmService.trackElement(
            `${eventData.optInResponse?.surveyName}-${eventData.optInResponse?.result}`,
            GaContext.QUALTRICS,
            ElementTypes.SURVEY,
            `${eventData.optInResponse?.result}`,
            ElementActions.CLICK
          );
          break;

        case QualtricsMessageTypes.NPS_RATING_RESPONSE:
          this.gtmService.trackElement(
            `${eventData.npsRatingResponse?.surveyName}-${eventData.npsRatingResponse?.result}`,
            GaContext.QUALTRICS,
            ElementTypes.SURVEY,
            `${eventData.npsRatingResponse?.result}`,
            ElementActions.CLICK
          );
          break;

        case QualtricsMessageTypes.SURVEY_DONE:
          this.gtmService.trackElement(
            `${eventData.surveyDone}`,
            GaContext.QUALTRICS,
            ElementTypes.SURVEY,
            undefined,
            ElementActions.SUBMIT
          );
          break;

        case QualtricsMessageTypes.CONFSURVEY_DONE:
          this.gtmService.trackElement(
            `${eventData.confSurveyDone}`,
            GaContext.QUALTRICS,
            ElementTypes.SURVEY,
            this.survey.isCsatSurvey ? this.csatScore : undefined,
            ElementActions.SUBMIT
          );
          break;

        case QualtricsMessageTypes.CSAT_SCORE_SELECTED:
          this.csatScore = eventData.csatScoreSelected;
          break;

        default:
          break;
      }
    }
  }

  private messageHasRealValue(messageValue: string): boolean {
    return (
      messageValue.indexOf(QualtricsMessageTypes.OPTIN_RESPONSE) > -1 ||
      messageValue.indexOf(QualtricsMessageTypes.NPS_RATING_RESPONSE) > -1 ||
      messageValue.indexOf(QualtricsMessageTypes.SURVEY_DONE) > -1 ||
      messageValue.indexOf(QualtricsMessageTypes.CONFSURVEY_DONE) > -1 ||
      messageValue.indexOf(QualtricsMessageTypes.CSAT_SCORE_SELECTED) > -1
    );
  }
}
